import { finalize, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { LoadingService } from '../loading.service';
import { environment } from '@app/core/environments/environment';
import { Signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthSelectors } from '@app/auth/store/auth.selectors';
import { IAccountStateModel } from '@app/auth/store/auth.state.models';
import { Pagination } from '@app/core/models/client/account/pagination.models';
import { AllTransactionsResponse, DirectDebitResponse, IDateRange, IDDChargeData, IfilterPayload, TransactionDetailsResponse } from '@app/core/models/client/transactions/transaction.model';
import { PaymentApiResponse, PaymentPayload } from '@app/core/models/client/account/account-details.model';
@Injectable({
  providedIn: 'root'
})
export class TransactionsService {
  http = inject(HttpClient);
  loadingService = inject(LoadingService);
  store = inject(Store)


  activeAccountId: any = this.store.selectSnapshot(AuthSelectors.getActiveAccountID);
  profileId: any = this.store.selectSnapshot(AuthSelectors.getProfileID);
  activeAccount: any = this.store.selectSnapshot(AuthSelectors.getActiveAccount);
 
  

  BASE_URL = environment.CLIENT_BASE_URL + '/v1/';

  transactionRejected = signal<string | null>(null);
  
  constructor() { }

  rejectTransaction(transactionId: string,accountId: string) {
    const uniqueStateUrl = 'reject-transaction';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .post(`${this.BASE_URL}withdrawal/cancel-wallet-to-bank-withdrawal-request`, { transaction_id: transactionId, account_id: accountId })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  private convertToKobo(amount: number): number {
    return amount * 100;
  }

  createVirtualAccount() {    
    return this.http.post(this.BASE_URL + 'bank/create-static-virtual-account', {
      account_id: this.activeAccountId,
      account_name: this.activeAccount.name,
    });
  }

  getCardPaymentLink(amount: number, accountId: string, paymentGateway: string): Observable<PaymentApiResponse> {
    const uniqueStateUrl = 'get-card-payment-link';
    this.loadingService.setLoading(uniqueStateUrl, true);
    const url = paymentGateway === 'flutterwave' 
      ? this.BASE_URL + 'payment/get-card-payment-link' 
      : this.BASE_URL + 'payment/initialize-paystack-payment';
   
    const payload: PaymentPayload = {
      amount: amount,
      account_id: accountId,
      currency: 'NGN',
    };

    if (paymentGateway == 'flutterwave') {
      payload.profile_id = this.profileId;
    }

    return this.http.post<PaymentApiResponse>(url, payload).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }
 
   getTransactionByTransRef(flutterTxnRef: string) {
    const uniqueStateUrl = 'get-transactions';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<AllTransactionsResponse>(
        `${this.BASE_URL}payment/get-transaction-ref?initiation_transfer_ref=${flutterTxnRef}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  
  getTransactions(data: Pagination = new Pagination(), accountId: string) {
    const uniqueStateUrl = 'get-transactions';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<AllTransactionsResponse>(
        `${this.BASE_URL}account/get-transactions-by-account?take=${data.size}&skip=${data.skip}&account_id=${accountId}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  filterTransactions(queryParams: string) {
    const uniqueStateUrl = 'get-transactions';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http.get(`${this.BASE_URL}bank/transaction-filter?${queryParams}`).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }
  
  searchTransactions(queryParams: string) {
    const uniqueStateUrl = 'get-transactions';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http.get(`${this.BASE_URL}bank/transaction-filter?${queryParams}`).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }
  getTransactionDetails(transactionId: string) {
    const uniqueStateUrl = 'get-transaction-details';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<TransactionDetailsResponse>(
        `${this.BASE_URL}bank/get-transaction-by-id?transaction_id=${transactionId}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }


  // For Normal Statement
  getTransactionStatement(dateRange: IDateRange) {
    const uniqueStateUrl = 'get-transaction-statement';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http.post(this.BASE_URL + 'account/transaction-statement-generate', {
      ...dateRange,
      account_id: this.activeAccountId,
    }).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }

  emailTransactionStatement(dateRange: IDateRange) {
    const uniqueStateUrl = 'email-transaction-statement';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http.post(this.BASE_URL + 'account/send-transactions-statement-via-email', {
      ...dateRange,
      account_id: this.activeAccountId,
    }).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }

  generateStatement(
    endpoint: string,
    uniqueStateUrl: string,
    dateRange: IDateRange,
    type: 'report_only' | 'report_and_email'
  ) {
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http.get(
      this.BASE_URL + endpoint,
      {
        params: {
          ...dateRange,
          account_id: this.activeAccountId,
          type
        }
      }
    ).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  };

  chargeDirectDebit(data: IDDChargeData) {
    const uniqueStateUrl = 'direct-debit-charge';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http.post(this.BASE_URL + 'payment/direct-debit-charge', data).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }

  // Mutual Funds
  getMutualFundsStatement(dateRange: IDateRange) {
    return this.generateStatement(
      'report/generate-mutual-fund-statement',
      'get-mutual-fund-statement',
      dateRange,
      'report_only'
    );
  };

  emailMutualFundsStatement(dateRange: IDateRange) {
    return this.generateStatement(
      'report/generate-mutual-fund-statement',
      'email-mutual-fund-statement',
      dateRange,
      'report_and_email'
    );
  };

  // LCM
  getLCMStatement(dateRange: IDateRange) {
    return this.generateStatement(
      'account/lcm/statement-generate', //change to correct endpoint later
      'get-lcm-statement',
      dateRange,
      'report_only'
    );
  };

  emailLCMStatement(dateRange: IDateRange) {
    return this.generateStatement(
      'account/lcm/statement-generate', //change to correct endpoint later
      'email-lcm-statement',
      dateRange,
      'report_and_email'
    );
  };

  // PMS
  getPMSStatement(dateRange: IDateRange) {
    return this.generateStatement(
      'account/pms/statement-generate', //change to correct endpoint later
      'get-pms-statement',
      dateRange,
      'report_only'
    );
  };

  emailPMSStatement(dateRange: IDateRange) {
    return this.generateStatement(
      'account/pms/statement-generate', //change to correct endpoint later
      'email-pms-statement',
      dateRange,
      'report_and_email'
    );
  };

  generateMutualFundStatementForFund(
    type: 'report_only' | 'report_and_email',
    timeframe: string,
    dateRange?: IDateRange
  ) {
    const uniqueStateUrl = type === 'report_only' ? 'get-fund-statement' : 'email-fund-statement';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http.get(this.BASE_URL + 'report/generate-client-mutual-fund-statement', {
      params: {
        ...(dateRange ? { ...dateRange } : {}),
        account_id: this.activeAccountId,
        type,
        time_frame : timeframe,
      },
    }).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }


  initialiseDirectDebit(accountId: string) {
    const uniqueStateUrl = 'initialise-direct-debit';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http.post<DirectDebitResponse>(`${this.BASE_URL}payment/initialize-direct-debit`, {account_id: accountId}).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }
}

